import { ACTIVE_STATUS } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'

/**
 * The winter service providers (subcontractors and their operators) are reported by
 * the backend in a nested shape - the address sits on `geolocation`, the contact on
 * `responsible_person`, the status is a boolean - while the forms edit one flat
 * field per value. This is the single place that translation lives, so a details
 * form, a create form and the operators tab can never read or write a record
 * differently.
 */

/**
 * The `is_active` flag as the status the dropdowns and the table cells speak.
 */
export function toProviderStatus (record) {
  return record?.is_active === false ? ACTIVE_STATUS.inactive : ACTIVE_STATUS.active
}

/**
 * Backend record -> the flat keys the form fields are bound to.
 */
export function toProviderForm (record) {
  const {
    geolocation,
    responsible_person: responsiblePerson,
    base
  } = record || {}

  return {
    address: geolocation?.address ?? null,
    latitude: geolocation?.latitude ?? null,
    longitude: geolocation?.longitude ?? null,
    status: toProviderStatus(record),
    base_id: base?.id ?? null,
    responsible_person_name: responsiblePerson?.name ?? null,
    responsible_person_email: responsiblePerson?.email ?? null,
    responsible_person_phone: responsiblePerson?.phone ?? null
  }
}

/**
 * Writes the flat keys onto the record itself. Content.vue hands `onDataReceived` the
 * very object it then turns into the form data, so the flat values have to land on it
 * rather than on a copy - that also keeps the untouched copy it restores on cancel
 * flat, instead of emptying every field.
 */
export function applyProviderForm (record) {
  if (!record) {
    return record
  }

  return Object.assign(record, toProviderForm(record))
}

/**
 * The same translation as a fresh object. Used after a save: the record Content.vue
 * already holds has been made reactive, so keys added to it afterwards would not be
 * picked up - it has to be handed a new object instead.
 */
export function withProviderForm (record) {
  return {
    ...record,
    ...toProviderForm(record)
  }
}

/**
 * The status dropdown edits what the backend keeps as `is_active`. It is sent as 1/0
 * rather than true/false because the details forms are posted as FormData, and
 * Laravel's `boolean` rule rejects the string "true".
 */
export function applyProviderStatusToPayload (data) {
  if (!data) {
    return data
  }

  const { status, ...payload } = data

  if (status === undefined) {
    return payload
  }

  return {
    ...payload,
    is_active: status === ACTIVE_STATUS.inactive ? 0 : 1
  }
}
